import React, { useCallback, useEffect, useState } from "react";
import { IoPaperPlane } from "react-icons/io5";
import { Timestamp } from "firebase/firestore";
import { useUser } from "../../../context/UserContext.tsx";
import { Messaging } from "../../../models/Messaging.tsx";
import {
    deleteMessageForUser,
    getAgentOfPlayer,
    isMessageUnread,
    markMessageAsRead,
    messaging,
    showMessaging,
    showMessagingSent,
} from "../../../services/player/Messaging.tsx";
import "../../../styles/PlayerMail.css";

const AVATAR_COLORS = ["#2f9e4f", "#3b6fd8", "#e07a9a", "#8b6bb8", "#d9a441", "#2a9d8f"];

function avatarColor(name: string) {
    let hash = 0;
    for (let i = 0; i < name.length; i += 1) hash = (hash + name.charCodeAt(i)) % AVATAR_COLORS.length;
    return AVATAR_COLORS[hash];
}

function formatMessageDate(seconds: number) {
    const date = new Date(seconds * 1000);
    const day = date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const time = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    return `${day} at ${time}`;
}

function previewText(value: string) {
    return value.replace(/\s+/g, " ").trim();
}

function PlayerMail() {
    const { user } = useUser();
    const [tab, setTab] = useState<"inbox" | "sent">("inbox");
    const [inbox, setInbox] = useState<Messaging[]>([]);
    const [sent, setSent] = useState<Messaging[]>([]);
    const [agentName, setAgentName] = useState<string | null>(null);
    const [openId, setOpenId] = useState<string | null>(null);
    const [subject, setSubject] = useState("");
    const [draft, setDraft] = useState("");
    const [reply, setReply] = useState("");
    const [notice, setNotice] = useState("");
    const [error, setError] = useState("");
    const [isSending, setIsSending] = useState(false);

    const userName = user?.userName ?? "";

    const load = useCallback(async () => {
        if (!userName) return;
        const [inboxRows, sentRows, agent] = await Promise.all([
            showMessaging(userName),
            showMessagingSent(userName),
            getAgentOfPlayer(userName),
        ]);
        setInbox(inboxRows);
        setSent(sentRows);
        setAgentName(agent);
    }, [userName]);

    useEffect(() => {
        load().catch((loadError) => console.error("Error fetching messages:", loadError));
    }, [load]);

    const rows = (tab === "inbox" ? inbox : sent)
        .filter((message) => !message.deletedBy?.includes(userName))
        .sort((a, b) => b.createDate.seconds - a.createDate.seconds);

    const openMessage = async (message: Messaging) => {
        const id = message.id || "";
        const next = openId === id ? null : id;
        setOpenId(next);
        setReply("");
        setError("");
        if (next && tab === "inbox" && isMessageUnread(message, userName)) {
            try {
                await markMessageAsRead(id, userName);
                setInbox((prev) => prev.map((row) => (
                    row.id === id ? { ...row, readBy: [...(row.readBy || []), userName] } : row
                )));
            } catch (readError) {
                console.error("Error marking message as read:", readError);
            }
        }
    };

    const showNotice = (text: string) => {
        setNotice(text);
        setTimeout(() => setNotice(""), 3000);
    };

    const sendNew = async (event: React.FormEvent) => {
        event.preventDefault();
        const title = subject.trim();
        const body = draft.trim();
        if (!agentName) {
            setError("Your agent could not be found. Please contact support.");
            return;
        }
        if (!title || !body) {
            setError("Write a title and a message before sending.");
            return;
        }
        setIsSending(true);
        setError("");
        try {
            await messaging({
                senderUser: userName,
                recipientUsers: [agentName],
                messageBody: body,
                subject: title,
                createDate: Timestamp.now(),
            });
            setSubject("");
            setDraft("");
            showNotice("Message sent.");
            await load();
        } catch {
            setError("Error sending message. Please try again.");
        } finally {
            setIsSending(false);
        }
    };

    const sendReply = async (message: Messaging) => {
        const body = reply.trim();
        if (!body) {
            setError("Type something to send your reply.");
            return;
        }
        setIsSending(true);
        setError("");
        try {
            await messaging({
                subject: message.subject.startsWith("Re:") ? message.subject : `Re: ${message.subject}`,
                messageBody: body,
                senderUser: userName,
                recipientUsers: [message.senderUser],
                createDate: Timestamp.now(),
                replyToMessageId: message.id,
            });
            setReply("");
            setOpenId(null);
            showNotice("Reply sent.");
            await load();
        } catch {
            setError("Error sending reply. Please try again.");
        } finally {
            setIsSending(false);
        }
    };

    const removeMessage = async (messageId: string) => {
        try {
            await deleteMessageForUser(messageId, userName);
            setInbox((prev) => prev.filter((row) => row.id !== messageId));
            setSent((prev) => prev.filter((row) => row.id !== messageId));
            setOpenId(null);
        } catch (deleteError) {
            console.error("Error deleting message:", deleteError);
            setError("Could not delete that message.");
        }
    };

    if (!userName) return null;

    return (
        <div className="player-mail">
            <section className="player-mail__card">
                <header className="player-mail__head">
                    <div>
                        <h1>{tab === "inbox" ? "Inbox" : "Sent"}</h1>
                        <p>{tab === "inbox" ? "Messages from your agents" : "Messages you sent"}</p>
                    </div>
                    <div className="player-mail__switch" role="tablist">
                        <button type="button" className={tab === "inbox" ? "is-on" : ""} onClick={() => { setTab("inbox"); setOpenId(null); }}>
                            Inbox
                        </button>
                        <button type="button" className={tab === "sent" ? "is-on" : ""} onClick={() => { setTab("sent"); setOpenId(null); }}>
                            Sent
                        </button>
                    </div>
                </header>

                {rows.length === 0 ? (
                    <p className="player-mail__empty">
                        {tab === "inbox" ? "No messages yet." : "You haven't sent any messages yet."}
                    </p>
                ) : (
                    <ul className="player-mail__list">
                        {rows.map((message) => {
                            const person = tab === "inbox"
                                ? message.senderUser
                                : (message.recipientUsers?.join(", ") || "Agent");
                            const unread = tab === "inbox" && isMessageUnread(message, userName);
                            const open = openId === message.id;
                            return (
                                <li key={message.id} className={open ? "is-open" : ""}>
                                    <button type="button" className="player-mail__row" onClick={() => openMessage(message)}>
                                        <span className={`player-mail__dot${unread ? " is-unread" : ""}`} />
                                        <span className="player-mail__avatar" style={{ background: avatarColor(person) }}>
                                            {(person[0] || "?").toUpperCase()}
                                        </span>
                                        <span className="player-mail__copy">
                                            <span className={`player-mail__name${unread ? " is-unread" : ""}`}>{person}</span>
                                            <span className="player-mail__preview">{message.subject || previewText(message.messageBody)}</span>
                                        </span>
                                        <span className="player-mail__date">{formatMessageDate(message.createDate.seconds)}</span>
                                    </button>
                                    {open && (
                                        <div className="player-mail__detail">
                                            {message.subject && <p className="player-mail__subject">{message.subject}</p>}
                                            <p className="player-mail__body">{message.messageBody}</p>
                                            {tab === "inbox" && (
                                                <div className="player-mail__reply">
                                                    <input
                                                        value={reply}
                                                        onChange={(event) => setReply(event.target.value)}
                                                        placeholder="Write a reply..."
                                                    />
                                                    <button type="button" onClick={() => sendReply(message)} disabled={isSending}>
                                                        Reply
                                                    </button>
                                                </div>
                                            )}
                                            <button type="button" className="player-mail__delete" onClick={() => removeMessage(message.id || "")}>
                                                Delete
                                            </button>
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </section>

            <form className="player-mail__compose" onSubmit={sendNew}>
                <h2>Send message</h2>
                <input
                    value={subject}
                    onChange={(event) => setSubject(event.target.value)}
                    placeholder="Title"
                    aria-label="Title"
                />
                <input
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    placeholder="Type your message..."
                    aria-label="Message"
                />
                <button type="submit" disabled={isSending || !agentName}>
                    <IoPaperPlane aria-hidden="true" />
                    {isSending ? "Sending" : "Send"}
                </button>
                {notice && <p className="player-mail__notice">{notice}</p>}
                {error && <p className="player-mail__error">{error}</p>}
            </form>
        </div>
    );
}

export default PlayerMail;
